/**
 * Storage shim — prevents white-screen crashes when localStorage / sessionStorage
 * are unavailable (iframe sandboxes, private browsing, restricted environments).
 * Must be imported BEFORE any other module that touches storage.
 */

function createMemoryStorage(): Storage {
  const store = new Map<string, string>();
  return {
    get length() {
      return store.size;
    },
    clear() {
      store.clear();
    },
    getItem(key: string) {
      return store.get(key) ?? null;
    },
    key(index: number) {
      return [...store.keys()][index] ?? null;
    },
    removeItem(key: string) {
      store.delete(key);
    },
    setItem(key: string, value: string) {
      store.set(key, value);
    },
  };
}

function isStorageAvailable(storage: Storage): boolean {
  const testKey = "__storage_test__";
  try {
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

// Shim localStorage
try {
  if (!isStorageAvailable(window.localStorage)) {
    Object.defineProperty(window, "localStorage", { value: createMemoryStorage() });
  }
} catch {
  Object.defineProperty(window, "localStorage", { value: createMemoryStorage() });
}

// Shim sessionStorage
try {
  if (!isStorageAvailable(window.sessionStorage)) {
    Object.defineProperty(window, "sessionStorage", { value: createMemoryStorage() });
  }
} catch {
  Object.defineProperty(window, "sessionStorage", { value: createMemoryStorage() });
}
